import axios, { AxiosResponse, HttpStatusCode } from "axios";
import { AppConfig } from "../../AppConfig";
import { ApiRoutes, RETRY_DELAY, RETRY_MAX_COUNT } from "../../constants/api";
import { appActions } from "../../ducks/app";
import { authActions } from "../../ducks/auth";
import { Configuration } from "../api/configuration";
import {
  BannerSearchAPI,
  CampaignActiveAPI,
  CouponCheckAPI,
  CouponLimitedAPI,
  CouponRedeemAPI,
  CouponSearchAPI,
  CouponValidateAPI,
  LoginAPI,
  LogoutAPI,
  MenuLikeAPI,
  MenuLimitedAPI,
  MenuSearchAPI,
  OrderFinalizeAPI,
  OrderSearchAPI,
  OrderTemporaryAPI,
  PasswordChangeAPI,
  PasswordReminderAPI,
  PaymentUrlAPI,
  RecaptchaCheckAPI,
  StoreLikeAPI,
  StoreSearchAPI,
  UserAPI,
  UserExidAPI,
  UserMailChangeAPI,
  UserMailChangeSendAPI,
  UserMailCheckAPI,
  UserQuitAPI,
  UserRegisterAPI,
  UserTemporaryAPI,
} from "../api/requests";
import { MembershipAPI } from "../api/requests/auth/membership";
import { UserRegisterMailAPI } from "../api/requests/auth/register_mail";
import { UserTemporaryMailAPI } from "../api/requests/auth/temporary_mail";
import { UserEditAPI } from "../api/requests/auth/user_edit";
import { CouponExternalAPI } from "../api/requests/coupon/external";
import { OrderStatusAPI } from "../api/requests/order/status";
import { LocalStorageManager } from "../storage/LocalStorageManager";
import { APIError } from "../types";
import { ErrorMessageManager } from "./ErrorMessageManager";

const API_BASE_URL = process.env.REACT_APP_API_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const WITH_CREDENTIALS = process.env.REACT_APP_WITH_CREDENTIALS === "true";
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === "true";

const configuration = new Configuration({
  basePath: API_BASE_URL,
});

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: WITH_CREDENTIALS,
  retries: RETRY_MAX_COUNT,
  retryCount: 0,
  useMockApi: USE_MOCK_API,
});

// リクエスト前の処理
axiosInstance.interceptors.request.use((config) => {
  const accessToken = LocalStorageManager.Instance.getObject<string>(
    AppConfig.Instance.LocalStorageKey["accessToken"],
  );
  config.headers["x-joyfull-auth"] = accessToken ? `Bearer ${accessToken}` : "";
  config.headers["x-api-key"] = X_API_KEY;

  return config;
});

export const requestSuccess = (config: AxiosResponse<any, any>) => {
  return { ...config, response: { data: config.data } };
};

export const requestFailure = (error: APIError, store: any) => {
  const showErrorAlert = (errorCode: string) => {
    const errorMessage: string =
      ErrorMessageManager.Instance.getErrorMessage(errorCode);
    store.dispatch(
      appActions.showModal.started({
        title: errorMessage,
        nextButtonLabel: "閉じる",
        handleNext: () => store.dispatch(appActions.closeModal()),
      }),
    );
  };

  // タイムアウト(ECONNABORTED) もしくは 5xx エラーのリトライ処理
  if (
    error.code === "ECONNABORTED" ||
    error.response?.status.toString().substring(0, 1) === "5"
  ) {
    if (
      error.config &&
      (error.config.retries ?? 0) > (error.config.retryCount ?? 0)
    ) {
      error.config.retryCount = (error.config.retryCount ?? 0) + 1;

      return new Promise((resolve) => {
        setTimeout(() => {
          if (error.config) {
            resolve(axiosInstance?.request(error.config));
          }
        }, RETRY_DELAY);
      });
    } else {
      showErrorAlert(error.response?.data.data?.code ?? "9900-9999");
    }
  }

  // TODO: Update error handling
  if (!error) {
  } else if (error && error.code === "ERR_NETWORK") {
    showErrorAlert("0000-0000");
  } else if (
    error &&
    error.response?.status === HttpStatusCode.ServiceUnavailable
  ) {
    showErrorAlert("9900-9999");
  } else if (
    error &&
    error.response?.status === HttpStatusCode.Unauthorized &&
    !error.request.responseURL.includes(ApiRoutes.auth.login)
  ) {
    showErrorAlert("9010-1001");
    store.dispatch(authActions.logout.started({}));
  } else if (error && error.response?.status === HttpStatusCode.Forbidden) {
    showErrorAlert("9010-2001");
  } else {
    showErrorAlert(error.response?.data.data?.code ?? "9900-9999");
  }
  return Promise.reject({
    ...error,
    status: error.response?.status ?? HttpStatusCode.InternalServerError,
  });
};

const userExidApi = new UserExidAPI(configuration, axiosInstance);
const loginApi = new LoginAPI(configuration, axiosInstance);
const logoutApi = new LogoutAPI(configuration, axiosInstance);
const userMailChangeApi = new UserMailChangeAPI(configuration, axiosInstance);
const userMailChangeSendApi = new UserMailChangeSendAPI(
  configuration,
  axiosInstance,
);
const userMailCheckApi = new UserMailCheckAPI(configuration, axiosInstance);
const passwordChangeApi = new PasswordChangeAPI(configuration, axiosInstance);
const passwordReminderApi = new PasswordReminderAPI(
  configuration,
  axiosInstance,
);
const userQuitApi = new UserQuitAPI(configuration, axiosInstance);
const userRegisterApi = new UserRegisterAPI(configuration, axiosInstance);
const userRegisterMailApi = new UserRegisterMailAPI(
  configuration,
  axiosInstance,
);
const userTemporaryApi = new UserTemporaryAPI(configuration, axiosInstance);
const userTemporaryMailApi = new UserTemporaryMailAPI(
  configuration,
  axiosInstance,
);
const userApi = new UserAPI(configuration, axiosInstance);
const userEditApi = new UserEditAPI(configuration, axiosInstance);
const bannerSearchApi = new BannerSearchAPI(configuration, axiosInstance);
const campaignActiveApi = new CampaignActiveAPI(configuration, axiosInstance);
const couponCheckApi = new CouponCheckAPI(configuration, axiosInstance);
const couponExternalApi = new CouponExternalAPI(configuration, axiosInstance);
const couponLimitedApi = new CouponLimitedAPI(configuration, axiosInstance);
const couponSearchApi = new CouponSearchAPI(configuration, axiosInstance);
const couponValidateApi = new CouponValidateAPI(configuration, axiosInstance);
const couponRedeemApi = new CouponRedeemAPI(configuration, axiosInstance);
const membershipApi = new MembershipAPI(configuration, axiosInstance);
const menuLikeApi = new MenuLikeAPI(configuration, axiosInstance);
const menuLimitedApi = new MenuLimitedAPI(configuration, axiosInstance);
const menuSearchApi = new MenuSearchAPI(configuration, axiosInstance);
const orderStatusApi = new OrderStatusAPI(configuration, axiosInstance);
const orderFinalizeApi = new OrderFinalizeAPI(configuration, axiosInstance);
const orderSearchApi = new OrderSearchAPI(configuration, axiosInstance);
const orderTemporaryApi = new OrderTemporaryAPI(configuration, axiosInstance);
const paymentUrlApi = new PaymentUrlAPI(configuration, axiosInstance);
const recaptchaCheckApi = new RecaptchaCheckAPI(configuration, axiosInstance);
const storeLikeApi = new StoreLikeAPI(configuration, axiosInstance);
const storeSearchApi = new StoreSearchAPI(configuration, axiosInstance);

export {
  bannerSearchApi,
  campaignActiveApi,
  couponCheckApi,
  couponExternalApi,
  couponLimitedApi,
  couponRedeemApi,
  couponSearchApi,
  couponValidateApi,
  loginApi,
  logoutApi,
  membershipApi,
  menuLikeApi,
  menuLimitedApi,
  menuSearchApi,
  orderFinalizeApi,
  orderSearchApi,
  orderStatusApi,
  orderTemporaryApi,
  passwordChangeApi,
  passwordReminderApi,
  paymentUrlApi,
  recaptchaCheckApi,
  storeLikeApi,
  storeSearchApi,
  userApi,
  userEditApi,
  userExidApi,
  userMailChangeApi,
  userMailChangeSendApi,
  userMailCheckApi,
  userQuitApi,
  userRegisterApi,
  userRegisterMailApi,
  userTemporaryApi,
  userTemporaryMailApi,
};
